import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { toast } from 'sonner';
import { PageHeader } from '../components/common/PageHeader';
import { DatePicker } from '../components/common/DatePicker';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  CalendarDays, Clock, Users, Check, X, Trash2, Plus, Settings as SettingsIcon,
  Utensils, Scissors, Mail, Copy, Send, CalendarCheck, Phone, Loader2,
} from 'lucide-react';

const todayStr = () => new Date().toISOString().slice(0, 10);
const STATUS_CLASS = {
  pending: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  confirmed: 'bg-pos-free/10 text-pos-free border-pos-free/20',
  rejected: 'bg-destructive/10 text-destructive border-destructive/20',
  cancelled: 'bg-muted text-muted-foreground border-border',
  completed: 'bg-primary/10 text-primary border-primary/20',
};

export default function ReservationsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('list');
  const [settings, setSettings] = useState(null);
  const [tables, setTables] = useState([]);

  const [date, setDate] = useState(todayStr());
  const [statusFilter, setStatusFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const WEEKDAYS = [t('reservations.wd.mon'), t('reservations.wd.tue'), t('reservations.wd.wed'), t('reservations.wd.thu'), t('reservations.wd.fri'), t('reservations.wd.sat'), t('reservations.wd.sun')];
  const statusLabel = (s) => t(`reservations.status.${s}`, { defaultValue: s });

  const loadSettings = useCallback(async () => {
    const { data } = await api.get('/reservations/settings');
    setSettings(data);
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/reservations', { params: { from: date, to: date, status: statusFilter } });
      setItems(data || []);
    } catch (e) { toast.error(t('reservations.loadError')); }
    finally { setLoading(false); }
  }, [date, statusFilter, t]);

  useEffect(() => { loadSettings().catch(() => {}); api.get('/tables').then((r) => setTables(r.data || [])).catch(() => {}); }, [loadSettings]);
  useEffect(() => { loadList(); }, [loadList]);

  const isServices = settings?.model === 'servizi';

  const setStatus = async (id, status) => {
    try { await api.patch(`/reservations/${id}/status`, { status }); toast.success(t('reservations.statusUpdated')); loadList(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('reservations.error')); }
  };
  const assignTable = async (id, table_id) => {
    try { await api.patch(`/reservations/${id}/assign-table`, { table_id: table_id || null }); toast.success(t('reservations.tableUpdated')); loadList(); }
    catch (e) { toast.error(t('reservations.error')); }
  };
  const remove = async (id) => {
    if (!window.confirm(t('reservations.deleteConfirm'))) return;
    try { await api.delete(`/reservations/${id}`); toast.success(t('reservations.deleted')); loadList(); }
    catch (e) { toast.error(t('reservations.error')); }
  };

  const patchSettings = (patch) => setSettings((s) => ({ ...s, ...patch }));
  const saveSettings = async (extra) => {
    try {
      const payload = extra || settings;
      const { data } = await api.put('/reservations/settings', payload);
      setSettings(data);
      toast.success(t('reservations.settingsSaved'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('reservations.saveError')); }
  };

  const updateShift = (i, field, value) => {
    const shifts = [...(settings.shifts || [])];
    shifts[i] = { ...shifts[i], [field]: value };
    patchSettings({ shifts });
  };
  const addShift = () => patchSettings({ shifts: [...(settings.shifts || []), { name: t('reservations.shiftDefault'), start: '12:00', end: '15:00' }] });
  const removeShift = (i) => patchSettings({ shifts: settings.shifts.filter((_, idx) => idx !== i) });

  const updateService = (i, field, value) => {
    const services = [...(settings.services || [])];
    services[i] = { ...services[i], [field]: value };
    patchSettings({ services });
  };
  const addService = () => patchSettings({ services: [...(settings.services || []), { name: t('reservations.serviceDefault'), duration_minutes: 30, active: true }] });
  const removeService = (i) => patchSettings({ services: settings.services.filter((_, idx) => idx !== i) });

  const toggleWeekday = (d) => {
    const cur = settings.closed_weekdays || [];
    patchSettings({ closed_weekdays: cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d] });
  };

  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const sendTest = async () => {
    if (!testTo) { toast.error(t('reservations.enterEmail')); return; }
    setTesting(true);
    try { await api.post('/reservations/test-email', { to: testTo }); toast.success(t('reservations.testSent')); }
    catch (e) { toast.error(e?.response?.data?.detail || t('reservations.sendFailed')); }
    finally { setTesting(false); }
  };

  const embedCode = settings ? `<iframe src="${window.location.origin}/prenota/${settings.restaurant_id}" width="100%" height="760" style="border:0;max-width:640px" title="Prenotazioni"></iframe>` : '';
  const copyEmbed = () => { navigator.clipboard.writeText(embedCode); toast.success(t('reservations.codeCopied')); };

  if (!settings) {
    return <div className="p-6 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> {t('reservations.loading')}</div>;
  }

  return (
    <div className="p-4 md:p-6" data-testid="reservations-page">
      <PageHeader title={t('reservations.title')} subtitle={t('reservations.subtitle')} />

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <TabsList>
            <TabsTrigger value="list" data-testid="tab-list"><CalendarCheck className="h-4 w-4 mr-1.5" /> {t('reservations.tabReservations')}</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings"><SettingsIcon className="h-4 w-4 mr-1.5" /> {t('reservations.tabSettings')}</TabsTrigger>
          </TabsList>
          {tab === 'list' && (
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs">{t('reservations.date')}</Label>
                <DatePicker value={date} onChange={setDate} className="w-44 mt-1" data-testid="res-date-filter" />
              </div>
              <div>
                <Label className="text-xs">{t('reservations.statusFilter')}</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-44" data-testid="res-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('reservations.all')}</SelectItem>
                    <SelectItem value="pending">{t('reservations.status.pending')}</SelectItem>
                    <SelectItem value="confirmed">{t('reservations.confirmedPl')}</SelectItem>
                    <SelectItem value="rejected">{t('reservations.rejectedPl')}</SelectItem>
                    <SelectItem value="cancelled">{t('reservations.cancelledPl')}</SelectItem>
                    <SelectItem value="completed">{t('reservations.completedPl')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        {/* -------- LISTA -------- */}
        <TabsContent value="list">

          {loading ? (
            <div className="text-muted-foreground p-6">{t('reservations.loading')}</div>
          ) : items.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground" data-testid="res-empty">
              <CalendarDays className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <div className="font-medium">{t('reservations.noReservations')}</div>
              <div className="text-sm">{t('reservations.noReservationsDesc')}</div>
            </Card>
          ) : (
            <div className="space-y-3">
              {items.map((r) => (
                <Card key={r.id} className="p-4" data-testid={`res-item-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-lg flex items-center gap-1.5"><Clock className="h-4 w-4 text-primary" /> {r.slot_start}</span>
                        <Badge variant="outline" className={`text-[10px] ${STATUS_CLASS[r.status] || STATUS_CLASS.pending}`}>{statusLabel(r.status)}</Badge>
                        {r.model === 'servizi'
                          ? <Badge variant="secondary" className="text-[10px]"><Scissors className="h-3 w-3 mr-1" />{r.service_name}</Badge>
                          : <Badge variant="secondary" className="text-[10px]"><Users className="h-3 w-3 mr-1" />{t('reservations.peopleShort', { count: r.people })}</Badge>}
                      </div>
                      <div className="mt-1 font-medium">{r.customer?.name} {r.customer?.surname}</div>
                      <div className="text-sm text-muted-foreground flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {r.customer?.phone}</span>
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {r.customer?.email}</span>
                      </div>
                      {r.preferences ? <div className="text-sm text-muted-foreground mt-1">{t('reservations.preferences')}: {r.preferences}</div> : null}
                      {r.allergens ? <div className="text-sm text-amber-600 mt-0.5">{t('reservations.allergens')}: {r.allergens}</div> : null}
                      {r.email_status === 'skipped' ? <div className="text-[11px] text-muted-foreground mt-1">{t('reservations.emailSkipped')}</div> : null}
                      {r.email_status === 'failed' ? <div className="text-[11px] text-destructive mt-1">{t('reservations.emailFailed')}</div> : null}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {r.status === 'pending' && (
                        <div className="flex gap-1.5">
                          <Button size="sm" className="rounded-lg bg-pos-free hover:bg-pos-free/90" onClick={() => setStatus(r.id, 'confirmed')} data-testid={`res-confirm-${r.id}`}><Check className="h-4 w-4 mr-1" /> {t('reservations.confirm')}</Button>
                          <Button size="sm" variant="outline" className="rounded-lg text-destructive" onClick={() => setStatus(r.id, 'rejected')} data-testid={`res-reject-${r.id}`}><X className="h-4 w-4 mr-1" /> {t('reservations.reject')}</Button>
                        </div>
                      )}
                      {r.status === 'confirmed' && (
                        <div className="flex gap-1.5">
                          <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setStatus(r.id, 'completed')} data-testid={`res-complete-${r.id}`}>{t('reservations.complete')}</Button>
                          <Button size="sm" variant="ghost" className="rounded-lg text-muted-foreground" onClick={() => setStatus(r.id, 'cancelled')}>{t('reservations.cancel')}</Button>
                        </div>
                      )}
                      {r.model !== 'servizi' && (r.status === 'confirmed' || r.status === 'pending') && (
                        <Select value={r.table_id || 'none'} onValueChange={(v) => assignTable(r.id, v === 'none' ? '' : v)}>
                          <SelectTrigger className="w-40 h-9" data-testid={`res-table-${r.id}`}><SelectValue placeholder={t('reservations.assignTable')} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{t('reservations.noTable')}</SelectItem>
                            {tables.map((tb) => <SelectItem key={tb.id} value={tb.id}>{tb.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={() => remove(r.id)} data-testid={`res-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* -------- IMPOSTAZIONI -------- */}
        <TabsContent value="settings">
          <div className="space-y-4">
            <Card className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold">{t('reservations.onlineActive')}</div>
                <Switch checked={settings.enabled} onCheckedChange={(v) => patchSettings({ enabled: v })} data-testid="set-enabled" />
              </div>
              <Label className="text-sm">{t('reservations.model')}</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <Button type="button" variant={!isServices ? 'default' : 'outline'} className="rounded-xl h-12" onClick={() => patchSettings({ model: 'gastronomia' })} data-testid="set-model-gastronomia"><Utensils className="h-4 w-4 mr-2" /> {t('reservations.gastronomy')}</Button>
                <Button type="button" variant={isServices ? 'default' : 'outline'} className="rounded-xl h-12" onClick={() => patchSettings({ model: 'servizi' })} data-testid="set-model-servizi"><Scissors className="h-4 w-4 mr-2" /> {t('reservations.services')}</Button>
              </div>
            </Card>

            <Card className="p-5">
              <div className="font-semibold mb-3">{t('reservations.timeSlots')}</div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <Label className="text-sm">{t('reservations.slotInterval')}</Label>
                  <Select value={String(settings.slot_interval_minutes)} onValueChange={(v) => patchSettings({ slot_interval_minutes: Number(v) })}>
                    <SelectTrigger className="mt-1" data-testid="set-interval"><SelectValue /></SelectTrigger>
                    <SelectContent>{[15, 30, 45, 60].map((n) => <SelectItem key={n} value={String(n)}>{n} {t('reservations.min')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">{isServices ? t('reservations.maxPerSlotServices') : t('reservations.maxCapacity')}</Label>
                  <Input type="number" value={settings.capacity_per_slot} onChange={(e) => patchSettings({ capacity_per_slot: Number(e.target.value) })} className="mt-1" data-testid="set-capacity" />
                </div>
              </div>

              <Label className="text-sm">{t('reservations.shifts')}</Label>
              <div className="space-y-2 mt-1.5">
                {(settings.shifts || []).map((sh, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`shift-row-${i}`}>
                    <Input value={sh.name} onChange={(e) => updateShift(i, 'name', e.target.value)} placeholder={t('reservations.shiftName')} className="flex-1" />
                    <Input type="time" value={sh.start} onChange={(e) => updateShift(i, 'start', e.target.value)} className="w-32" />
                    <span className="text-muted-foreground">→</span>
                    <Input type="time" value={sh.end} onChange={(e) => updateShift(i, 'end', e.target.value)} className="w-32" />
                    <Button size="icon" variant="ghost" className="text-destructive shrink-0" onClick={() => removeShift(i)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="rounded-lg" onClick={addShift} data-testid="add-shift"><Plus className="h-4 w-4 mr-1" /> {t('reservations.addShift')}</Button>
              </div>

              <div className="mt-4">
                <Label className="text-sm">{t('reservations.closedDays')}</Label>
                <div className="flex gap-1.5 mt-1.5 flex-wrap">
                  {WEEKDAYS.map((w, i) => (
                    <button key={i} onClick={() => toggleWeekday(i)} data-testid={`weekday-${i}`}
                      className={`h-10 w-12 rounded-lg border text-sm font-medium transition-colors ${(settings.closed_weekdays || []).includes(i) ? 'bg-destructive/10 text-destructive border-destructive/30' : 'border-border text-muted-foreground hover:border-primary'}`}>{w}</button>
                  ))}
                </div>
              </div>
              <div className="mt-4 max-w-xs">
                <Label className="text-sm">{t('reservations.bookableUntil')}</Label>
                <Input type="number" value={settings.advance_days} onChange={(e) => patchSettings({ advance_days: Number(e.target.value) })} className="mt-1" data-testid="set-advance" />
              </div>
            </Card>

            {isServices && (
              <Card className="p-5">
                <div className="font-semibold mb-3">{t('reservations.bookableServices')}</div>
                <div className="space-y-2">
                  {(settings.services || []).map((s, i) => (
                    <div key={s.id || i} className="flex items-center gap-2" data-testid={`service-row-${i}`}>
                      <Input value={s.name} onChange={(e) => updateService(i, 'name', e.target.value)} placeholder={t('reservations.serviceName')} className="flex-1" />
                      <Input type="number" value={s.duration_minutes} onChange={(e) => updateService(i, 'duration_minutes', Number(e.target.value))} className="w-28" placeholder={t('reservations.min')} />
                      <span className="text-xs text-muted-foreground">{t('reservations.min')}</span>
                      <Switch checked={s.active !== false} onCheckedChange={(v) => updateService(i, 'active', v)} />
                      <Button size="icon" variant="ghost" className="text-destructive shrink-0" onClick={() => removeService(i)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" className="rounded-lg" onClick={addService} data-testid="add-service"><Plus className="h-4 w-4 mr-1" /> {t('reservations.addService')}</Button>
                </div>
              </Card>
            )}

            <Card className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{t('reservations.manualApproval')}</div>
                  <div className="text-sm text-muted-foreground">{t('reservations.manualApprovalDesc')}</div>
                </div>
                <Switch checked={settings.approval_threshold_enabled} onCheckedChange={(v) => patchSettings({ approval_threshold_enabled: v })} data-testid="set-approval-toggle" />
              </div>
              {settings.approval_threshold_enabled && (
                <div className="mt-3 max-w-xs">
                  <Label className="text-sm">{t('reservations.peopleThreshold')}</Label>
                  <Input type="number" value={settings.approval_threshold_people} onChange={(e) => patchSettings({ approval_threshold_people: Number(e.target.value) })} className="mt-1" data-testid="set-approval-threshold" />
                </div>
              )}
            </Card>

            <Card className="p-5">
              <div className="font-semibold mb-1">{t('reservations.emailMessages')}</div>
              <p className="text-xs text-muted-foreground mb-3">{t('reservations.emailVars')}: {'{nome} {cognome} {data} {ora} {persone} {servizio} {attivita}'}</p>
              {[
                ['confirmed', t('reservations.status.confirmed')],
                ['pending', t('reservations.status.pending')],
                ['rejected', t('reservations.msgRejected')],
              ].map(([k, lbl]) => (
                <div key={k} className="mb-3">
                  <Label className="text-sm">{lbl} — {t('reservations.subject')}</Label>
                  <Input value={settings[`msg_${k}_subject`] || ''} onChange={(e) => patchSettings({ [`msg_${k}_subject`]: e.target.value })} className="mt-1" data-testid={`msg-${k}-subject`} />
                  <Textarea rows={3} value={settings[`msg_${k}_body`] || ''} onChange={(e) => patchSettings({ [`msg_${k}_body`]: e.target.value })} className="mt-1.5" data-testid={`msg-${k}-body`} />
                </div>
              ))}
            </Card>

            <Card className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold flex items-center gap-2"><Mail className="h-4 w-4" /> {t('reservations.smtpTitle')}</div>
                <Switch checked={settings.email_enabled} onCheckedChange={(v) => patchSettings({ email_enabled: v })} data-testid="set-email-enabled" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm">{t('reservations.smtpHost')}</Label><Input value={settings.smtp_host || ''} onChange={(e) => patchSettings({ smtp_host: e.target.value })} placeholder="smtp.gmail.com" className="mt-1" data-testid="smtp-host" /></div>
                <div><Label className="text-sm">{t('reservations.port')}</Label><Input type="number" value={settings.smtp_port || ''} onChange={(e) => patchSettings({ smtp_port: Number(e.target.value) })} placeholder="587" className="mt-1" data-testid="smtp-port" /></div>
                <div>
                  <Label className="text-sm">{t('reservations.security')}</Label>
                  <Select value={settings.smtp_security} onValueChange={(v) => patchSettings({ smtp_security: v })}>
                    <SelectTrigger className="mt-1" data-testid="smtp-security"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starttls">STARTTLS (587)</SelectItem>
                      <SelectItem value="ssl">SSL/TLS (465)</SelectItem>
                      <SelectItem value="none">{t('reservations.none')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-sm">{t('reservations.username')}</Label><Input value={settings.smtp_username || ''} onChange={(e) => patchSettings({ smtp_username: e.target.value })} placeholder="email@dominio.it" className="mt-1" data-testid="smtp-username" /></div>
                <div><Label className="text-sm">{t('reservations.password')}</Label><Input type="password" value={settings.smtp_password || ''} onChange={(e) => patchSettings({ smtp_password: e.target.value })} placeholder="••••••" className="mt-1" data-testid="smtp-password" /></div>
                <div><Label className="text-sm">{t('reservations.sender')}</Label><Input value={settings.mail_from || ''} onChange={(e) => patchSettings({ mail_from: e.target.value })} placeholder="prenotazioni@dominio.it" className="mt-1" data-testid="smtp-from" /></div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">{t('reservations.smtpHint')}</p>
              <div className="flex items-end gap-2 mt-3">
                <div className="flex-1"><Label className="text-sm">{t('reservations.sendTestTo')}</Label><Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="tuo@indirizzo.it" className="mt-1" data-testid="smtp-test-to" /></div>
                <Button variant="outline" className="rounded-xl" onClick={sendTest} disabled={testing} data-testid="smtp-test-button"><Send className="h-4 w-4 mr-2" /> {t('reservations.test')}</Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">{t('reservations.smsHint')}</p>
            </Card>

            <Card className="p-5">
              <div className="font-semibold mb-2">{t('reservations.embedTitle')}</div>
              <div className="rounded-lg bg-muted p-3 font-mono text-xs break-all" data-testid="embed-code">{embedCode}</div>
              <div className="flex gap-2 mt-2">
                <Button variant="outline" className="rounded-xl" onClick={copyEmbed} data-testid="embed-copy"><Copy className="h-4 w-4 mr-2" /> {t('reservations.copyCode')}</Button>
                <a href={`/prenota/${settings.restaurant_id}`} target="_blank" rel="noreferrer"><Button variant="ghost" className="rounded-xl">{t('reservations.previewPage')}</Button></a>
              </div>
            </Card>

            <div className="sticky bottom-4 flex justify-end">
              <Button className="rounded-xl h-12 px-8 shadow-lg" onClick={() => saveSettings()} data-testid="save-settings-button"><Check className="h-5 w-5 mr-2" /> {t('reservations.saveSettings')}</Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
